"use client";

import { Typography, styled } from "@mui/material";
import { Download } from "lucide-react";
import { AttachmentChip, PdfFileIcon } from "@/ui";

const FilesList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const FileRowButton = styled("button")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--space-3)",
  width: "100%",
  padding: "var(--space-4) var(--space-4)",
  background: "var(--bg-canvas, #ffffff)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-md)",
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "border-color var(--duration-fast) var(--easing-standard), background-color var(--duration-fast) var(--easing-standard)",
  "&:hover:not(:disabled)": {
    borderColor: "var(--border-strong)",
    background: "var(--bg-hover)",
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
});

const BareChip = styled(AttachmentChip)({
  flex: 1,
  minWidth: 0,
  width: "auto",
  maxWidth: "none",
  padding: 0,
  border: "none",
  background: "none",
});

const PdfIcon = () => <PdfFileIcon size={20} />;

const DownloadCta = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--space-1)",
  flexShrink: 0,
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--action-product)",
});

interface FilesTabProps {
  onDownloadOriginal: () => void;
  onDownloadFormatted: () => void;
  onDownloadFormattedOriginal: () => void;
  /** An uploaded CV file is stored. */
  hasResume: boolean;
  /** The word-for-word transcription behind the branded original CV exists. */
  hasRawResume: boolean;
}

export default function FilesTab({
  onDownloadOriginal,
  onDownloadFormatted,
  onDownloadFormattedOriginal,
  hasResume,
  hasRawResume,
}: FilesTabProps) {
  return (
    <FilesList role="list">
      <FileRowButton
        type="button"
        onClick={onDownloadOriginal}
        disabled={!hasResume}
        title={
          hasResume ? "Download original CV" : "No original CV available"
        }
      >
        <BareChip
          filename="Original CV"
          mediaType="application/pdf"
          size={0}
          metaText="PDF document"
          icon={<PdfIcon />}
        />
        {hasResume && (
          <DownloadCta>
            Download
            <Download size={14} />
          </DownloadCta>
        )}
      </FileRowButton>

      <FileRowButton
        type="button"
        onClick={onDownloadFormatted}
        title="Download branded Floats CV"
      >
        <BareChip
          filename="Branded Floats CV"
          mediaType="application/pdf"
          size={0}
          metaText="PDF document"
          icon={<PdfIcon />}
        />
        <DownloadCta>
          Download
          <Download size={14} />
        </DownloadCta>
      </FileRowButton>

      <FileRowButton
        type="button"
        onClick={onDownloadFormattedOriginal}
        disabled={!hasRawResume}
        title={
          hasRawResume
            ? "Download branded original CV"
            : "No original CV transcription available"
        }
      >
        <BareChip
          filename="Branded original CV"
          mediaType="application/pdf"
          size={0}
          metaText="PDF document"
          icon={<PdfIcon />}
        />
        {hasRawResume && (
          <DownloadCta>
            Download
            <Download size={14} />
          </DownloadCta>
        )}
      </FileRowButton>

      {!hasResume && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No original CV is available for this candidate.
        </Typography>
      )}
    </FilesList>
  );
}
